import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';
import GameChatPanel from '../../components/game/GameChatPanel';

const DISCUSSION_SECONDS = 60;

export default function GameDiscussionScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const { room, currentRound, isHost, advanceToVoting, advanceLocalPhase, messages } = useGame();

  const isLocal = room?.mode === 'LOCAL';
  const isRemote = room?.mode === 'REMOTE';

  const [timeLeft, setTimeLeft] = useState(DISCUSSION_SECONDS);
  const timerRef = useRef(null);
  const hasAdvanced = useRef(false);

  // Navigate when round status changes via subscription
  useEffect(() => {
    if (!room || !currentRound) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    } else if (currentRound.status === 'VOTING') {
      navigation.navigate(SCREEN_NAMES.GAME_VOTING);
    } else if (currentRound.status === 'IMPOSTOR_GUESS') {
      navigation.navigate(SCREEN_NAMES.GAME_IMPOSTOR_GUESS);
    } else if (currentRound.status === 'FINISHED') {
      navigation.navigate(SCREEN_NAMES.GAME_RESULT);
    }
  }, [room?.id, currentRound?.status]);

  // Countdown — only host/local advances at expiry; others wait for subscription
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          if ((isLocal || isHost) && !hasAdvanced.current) {
            hasAdvanced.current = true;
            handleAdvance();
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isLocal, isHost]);

  if (!room || !currentRound) return null;

  const clues = currentRound.clues ?? [];

  const playerName = (playerId) => {
    if (isLocal) return `Joueur ${playerId + 1}`;
    return room.players?.find((p) => p.userId === playerId)?.user?.firstName ?? 'Joueur';
  };

  const handleAdvance = async () => {
    try {
      if (isLocal) {
        advanceLocalPhase('VOTING');
        navigation.navigate(SCREEN_NAMES.GAME_VOTING);
      } else {
        await advanceToVoting();
        navigation.navigate(SCREEN_NAMES.GAME_VOTING);
      }
    } catch (e) {
      toast.show(e.message || 'Erreur', { type: 'danger' });
    }
  };

  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');

  return (
    <View style={styles.container}>
      <View style={styles.recapBlock}>
        <Text style={styles.recapTitle}>Récap des indices</Text>
        {clues.map((c) => (
          <Text key={c.id} style={styles.clueItem}>
            <Text style={styles.clueAuthor}>{playerName(c.playerId)} → </Text>
            <Text style={styles.clueWord}>"{c.word}"</Text>
          </Text>
        ))}
      </View>

      <View style={[styles.timerRow, timeLeft <= 10 && styles.timerRowUrgent]}>
        <Text style={styles.timerLabel}>Discussion —</Text>
        <Text style={[styles.timerValue, timeLeft <= 10 && styles.timerValueUrgent]}>
          {mins}:{secs}
        </Text>
        {(isHost || isLocal) && (
          <TouchableOpacity style={styles.advanceBtn} onPress={handleAdvance}>
            <Text style={styles.advanceBtnText}>Voter ›</Text>
          </TouchableOpacity>
        )}
      </View>

      {isRemote ? (
        <GameChatPanel messages={messages} />
      ) : (
        <View style={styles.localBody}>
          <Text style={styles.discussIcon}>💬</Text>
          <Text style={styles.discussTip}>Discutez à voix haute !</Text>
          <Text style={styles.discussSub}>Qui est l'imposteur ?</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  recapBlock: { padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee', backgroundColor: '#fafafa' },
  recapTitle: { fontWeight: '700', fontSize: 14, color: '#555', marginBottom: 6 },
  clueItem: { fontSize: 14, marginBottom: 2 },
  clueAuthor: { color: '#888' },
  clueWord: { color: '#222', fontWeight: '600' },
  timerRow: { flexDirection: 'row', alignItems: 'center', padding: 12, borderBottomWidth: 1, borderBottomColor: '#eee', backgroundColor: '#fff', gap: 8 },
  timerRowUrgent: { backgroundColor: '#fff5f5' },
  timerLabel: { fontWeight: '600', color: '#555', fontSize: 14 },
  timerValue: { fontSize: 22, fontWeight: '900', color: theme.colors.primary, flex: 1 },
  timerValueUrgent: { color: '#e53935' },
  advanceBtn: { backgroundColor: theme.colors.primary, paddingHorizontal: 16, paddingVertical: 6, borderRadius: 20 },
  advanceBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  localBody: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  discussIcon: { fontSize: 64 },
  discussTip: { fontSize: 22, fontWeight: '800', color: '#333' },
  discussSub: { fontSize: 15, color: '#888' },
});
